import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "@phosphor-icons/react";

const CONTACT_EMAIL = "privacy@vexamos.com";
const COMPANY = "Vidya Vikas Technocrats Services (OPC) Pvt Ltd.";
const APP_NAME = "Vexamos";
const EFFECTIVE_DATE = "August 14, 2026";
const WEBSITE = "https://www.vexamos.com";
const GOVERNING_LAW = "India";
const JURISDICTION = "Hyderabad, Telangana, India";

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-white text-zinc-900" data-testid="terms-page">
      {/* Top bar */}
      <header className="border-b border-zinc-200 sticky top-0 bg-white z-10">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-zinc-600 hover:text-zinc-900" data-testid="terms-back-link">
            <ArrowLeft size={14} weight="bold" /> Home
          </Link>
          <div className="font-display text-lg font-bold tracking-tight">EXAM<span className="text-blue-600">/</span>OS</div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 lg:px-12 py-12 lg:py-16">
        <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-3 font-semibold">Legal</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mb-3">Terms of Service</h1>
        <div className="text-sm text-zinc-500 mb-10">
          Effective date: <span className="font-medium text-zinc-900" data-testid="terms-effective-date">{EFFECTIVE_DATE}</span> ·
          &nbsp;Applies to the {APP_NAME} web and mobile applications.
        </div>

        <div className="prose prose-zinc max-w-none space-y-8">
          <Section title="1. Acceptance of Terms">
            <p>
              These Terms of Service (&ldquo;Terms&rdquo;) form a binding agreement between you and{" "}
              {COMPANY} (&ldquo;{APP_NAME},&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;) governing your
              access to and use of the {APP_NAME} platform, including our website
              (<a className="link-underline" href={WEBSITE}>{WEBSITE}</a>) and mobile application (collectively, the &ldquo;Service&rdquo;).
            </p>
            <p>
              By creating an account, signing in, or otherwise using the Service, you confirm that you have read, understood
              and agree to be bound by these Terms and by our{" "}
              <Link to="/privacy" className="link-underline font-medium text-zinc-900" data-testid="terms-link-privacy">Privacy Policy</Link>.
              If you do not agree, please do not use the Service.
            </p>
          </Section>

          <Section title="2. Eligibility & Accounts">
            <ul>
              <li>You must be at least 13 years old to use {APP_NAME}. Learners under 13 may only use the Service under the supervision of a parent or an authorised institution.</li>
              <li>You are responsible for providing accurate registration information (name, email, role, institution code) and for keeping it up to date.</li>
              <li>You must complete email verification (6-digit OTP) before your account becomes active. Google Sign-In accounts are considered verified.</li>
              <li>You are responsible for all activity that occurs under your account and for keeping your password confidential. Notify us immediately at <a className="link-underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> of any unauthorised access.</li>
            </ul>
          </Section>

          <Section title="3. Roles & Permitted Use">
            <p>The Service supports the following roles:</p>
            <ul>
              <li><strong>Student / Learner</strong> — takes exams, views results, downloads certificates.</li>
              <li><strong>Examiner</strong> — creates questions, builds exams, reviews attempts, issues certificates for their institution.</li>
              <li><strong>Institution Admin</strong> — manages users, exams, branding and billing for their institution.</li>
              <li><strong>Proctor</strong> — monitors live exam attempts assigned to them.</li>
              <li><strong>Super Admin</strong> — Vexamos platform staff only.</li>
            </ul>
            <p>You agree to use the Service only for its intended purpose &mdash; delivering, taking or administering assessments and certifications.</p>
          </Section>

          <Section title="4. Content You Provide">
            <p>You retain ownership of the content you upload &mdash; questions, exams, attempts, certificate assets, images and any other material (&ldquo;Your Content&rdquo;).</p>
            <p>By uploading Your Content you grant {APP_NAME} a non-exclusive, worldwide, royalty-free licence to host, store, transmit, display and process Your Content <strong>solely to operate and improve the Service</strong> for you and your institution. We do not use Your Content for advertising or resell it.</p>
            <p>You warrant that Your Content does not infringe any third-party intellectual property, privacy or other rights, and does not contain unlawful, defamatory or harmful material.</p>
          </Section>

          <Section title="5. Exam Integrity & Proctoring">
            <ul>
              <li>Students agree not to cheat, share answers, use unauthorised aids or impersonate another person during any exam.</li>
              <li>When taking a proctored exam you consent to <strong>camera and microphone access</strong> and to the collection of periodic snapshots and audio-level samples for the sole purpose of exam integrity.</li>
              <li>Violation events (multiple faces, tab-switch, extended absence, audio spikes, etc.) are logged and may be reviewed by your examiner or proctor.</li>
              <li>Examiners and institution admins may revoke certificates or invalidate attempts based on verified integrity violations.</li>
            </ul>
          </Section>

          <Section title="6. Certificates & Verification">
            <p>Certificates issued through the Service include a unique certificate ID and a QR code linking to a public verification page. Anyone with the QR / link may verify the certificate&rsquo;s authenticity. Certificates remain the property of the issuing institution, and can be revoked by the institution if a violation is confirmed.</p>
          </Section>

          <Section title="7. Prohibited Conduct">
            <p>You agree not to:</p>
            <ul>
              <li>Reverse-engineer, decompile or attempt to derive the source code of the Service.</li>
              <li>Interfere with, disrupt or attack the Service (DDoS, spamming, injection, scraping at abusive rates).</li>
              <li>Circumvent access controls, quotas or proctoring measures.</li>
              <li>Upload malware, illegal, hateful or sexually-explicit content.</li>
              <li>Use the Service to build a competing product or resell it without written permission.</li>
              <li>Attempt to access another user&rsquo;s account, exam attempts or certificates without authorisation.</li>
            </ul>
            <p>We may suspend or terminate accounts that violate these rules, with or without notice.</p>
          </Section>

          <Section title="8. Payments & Subscriptions">
            <ul>
              <li>Some features are offered under paid plans (Basic / Pro / Institutional). Prices, features and limits are shown at{" "}
                <a className="link-underline" href="/#pricing">/#pricing</a> and are exclusive of applicable taxes.
              </li>
              <li>Payments are processed by third-party payment processors (e.g. Stripe / Razorpay). Their terms and privacy policies apply to the payment transaction.</li>
              <li>Subscriptions renew automatically unless cancelled before the renewal date. You may cancel at any time from your institution settings or by writing to us; the cancellation takes effect at the end of the current billing cycle.</li>
              <li><strong>Refunds:</strong> we do not offer refunds for partial months. If you believe you were charged in error, contact us within 7 days at <a className="link-underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.</li>
            </ul>
          </Section>

          <Section title="9. Third-Party Services">
            <p>The Service integrates with third-party providers (Resend, Judge0, OpenAI / Anthropic / Google Gemini, Stripe / Razorpay, Google Sign-In, Emergent Managed Object Storage). Your use of the Service through these integrations is subject to the third parties&rsquo; own terms; we are not responsible for their acts or omissions.</p>
          </Section>

          <Section title="10. Intellectual Property">
            <p>{APP_NAME}, our logo, trademarks and the underlying software are owned by {COMPANY} and are protected by intellectual property laws. Except for the licence you retain over Your Content, all rights are reserved.</p>
          </Section>

          <Section title="11. Termination">
            <p>You may delete your account at any time from your profile page or by writing to us. We may suspend or terminate your access to the Service if you materially breach these Terms, or if required by law. Upon termination, provisions that by their nature should survive (e.g. IP ownership, warranty disclaimers, liability limits, governing law) will remain in effect.</p>
          </Section>

          <Section title="12. Disclaimers">
            <p>The Service is provided on an &ldquo;as is&rdquo; and &ldquo;as available&rdquo; basis. To the fullest extent permitted by law we disclaim all implied warranties including merchantability, fitness for a particular purpose and non-infringement. We do not warrant that the Service will be uninterrupted, error-free, or that exam-integrity signals will detect every possible cheating attempt.</p>
          </Section>

          <Section title="13. Limitation of Liability">
            <p>To the maximum extent permitted by law, in no event shall {COMPANY} or its affiliates be liable for any indirect, incidental, special, consequential or punitive damages, or any loss of profits, revenue, data or goodwill arising out of or related to your use of the Service. Our aggregate liability under these Terms shall not exceed the greater of (a) the total fees paid by you to us in the 12 months preceding the claim, or (b) INR 5,000 (or its local currency equivalent).</p>
          </Section>

          <Section title="14. Indemnity">
            <p>You agree to indemnify and hold {APP_NAME} harmless from any claim, damages or expenses (including reasonable attorney&rsquo;s fees) arising out of your breach of these Terms, misuse of the Service, or infringement of any third-party rights by Your Content.</p>
          </Section>

          <Section title="15. Governing Law & Disputes">
            <p>These Terms are governed by the laws of {GOVERNING_LAW}, without regard to conflict of law principles. You agree that the courts of {JURISDICTION} shall have exclusive jurisdiction over any dispute arising out of or in connection with these Terms, subject to your rights under applicable consumer law.</p>
          </Section>

          <Section title="16. Changes to the Terms">
            <p>We may update these Terms from time to time. When we do, we will change the &ldquo;Effective date&rdquo; above and, for material changes, notify you by email or an in-app banner. Continued use of the Service after the changes take effect constitutes acceptance of the updated Terms.</p>
          </Section>

          <Section title="17. Contact Us">
            <p>
              Questions about these Terms?
              <br />
              Email: <a className="link-underline font-medium text-zinc-900" href={`mailto:${CONTACT_EMAIL}`} data-testid="terms-contact-email">{CONTACT_EMAIL}</a>
              <br />
              Website: <a className="link-underline font-medium text-zinc-900" href={WEBSITE}>{WEBSITE}</a>
              <br />
              WhatsApp support: <a className="link-underline font-medium text-zinc-900" href="https://wa.me/919154637357" target="_blank" rel="noreferrer">+91 91546 37357</a>
              <br />
              Operator: {COMPANY}
            </p>
          </Section>
        </div>

        <div className="mt-16 border-t border-zinc-200 pt-6 text-xs text-zinc-500 flex items-center justify-between">
          <Link to="/" className="hover:text-zinc-900" data-testid="terms-home-link">← Back to home</Link>
          <div className="font-mono">v1.0</div>
        </div>
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-display text-2xl font-bold tracking-tight mb-3 mt-2">{title}</h2>
      <div className="text-sm text-zinc-700 leading-relaxed space-y-3">{children}</div>
    </section>
  );
}
